'use client';

import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { FormInput } from '@/components/forms/form-input';
import { FormSelect } from '@/components/forms/form-select';
import { FormCombobox } from '@/components/forms/form-combobox';
import { useInfiniteSelect } from '@/hooks/use-infinite-select';
import { containerTypeKeys } from '../services/container-types.queries';
import { containerTypesApi } from '../services/container-types.api';
import {
  createSubContainerTypeSchema,
  type SubContainerTypeFormValues
} from '../sub-container-types.schemas';
import {
  useCreateSubContainerTypeMutation,
  useUpdateSubContainerTypeMutation
} from '../services/sub-container-types.mutations';
import type { SubContainerType } from '../sub-container-types.types';

interface SubContainerTypeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subContainerType?: SubContainerType | null;
}

export function SubContainerTypeFormDialog({
  open,
  onOpenChange,
  subContainerType
}: SubContainerTypeFormDialogProps) {
  const t = useTranslations('containerTypes');
  const tv = useTranslations('validation');
  const isEditing = !!subContainerType;

  const schema = useMemo(() => createSubContainerTypeSchema(tv), [tv]);

  const form = useForm<SubContainerTypeFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      container_type_id: '',
      status: 'active'
    }
  });

  const containerTypesSelect = useInfiniteSelect({
    queryKey: containerTypeKeys.lists(),
    queryFn: (params) => containerTypesApi.getAll(params),
    mapOption: useCallback(
      (ct: { id: number; name: string }) => ({
        value: String(ct.id),
        label: ct.name
      }),
      []
    ),
    params: { is_active: true }
  });

  const createMutation = useCreateSubContainerTypeMutation();
  const updateMutation = useUpdateSubContainerTypeMutation();
  const isPending = createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (open && subContainerType) {
      form.reset({
        name: subContainerType.name,
        container_type_id: String(subContainerType.container_type_id),
        status: subContainerType.status
      });
    } else if (open) {
      form.reset({
        name: '',
        container_type_id: '',
        status: 'active'
      });
    }
  }, [open, subContainerType, form]);

  async function onSubmit(values: SubContainerTypeFormValues) {
    const payload = {
      ...values,
      container_type_id: parseInt(values.container_type_id, 10)
    };

    if (isEditing && subContainerType) {
      await updateMutation.mutateAsync({
        id: subContainerType.id,
        data: payload
      });
    } else {
      await createMutation.mutateAsync(payload);
    }
    onOpenChange(false);
  }

  const statusOptions = [
    { value: 'active', label: t('subStatusActive') },
    { value: 'inactive', label: t('subStatusInactive') }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle>
            {isEditing
              ? t('subEditSubContainerType')
              : t('subCreateSubContainerType')}
          </DialogTitle>
        </DialogHeader>

        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
          <div className='space-y-4'>
            <FormInput
              control={form.control}
              name='name'
              label={t('subName')}
              placeholder={t('subNamePlaceholder')}
              required
            />

            <FormCombobox
              control={form.control}
              name='container_type_id'
              label={t('subParentContainerType')}
              infiniteConfig={containerTypesSelect}
              placeholder={t('subParentContainerTypePlaceholder')}
              required
            />

            <FormSelect
              control={form.control}
              name='status'
              label={t('subStatus')}
              options={statusOptions}
              required
            />

            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => onOpenChange(false)}
              >
                {t('subCancel')}
              </Button>
              <Button type='submit' disabled={isPending}>
                {isPending && <Loader2 className='me-2 h-4 w-4 animate-spin' />}
                {isEditing ? t('subSaveChanges') : t('subCreate')}
              </Button>
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
